/**
 * Keycloak OIDC client for Admin Suite.
 */

import Keycloak from 'keycloak-js';
import {
	syncTokenToStorage,
	hasStoredKeycloakSession,
	persistKeycloakSession,
	readStoredKeycloakSession,
} from './auth-utils';

export {
	clearKeycloakSessionStorage,
	hasStoredKeycloakSession,
	mapKeycloakUser,
	persistKeycloakSession,
	readStoredKeycloakSession,
	syncTokenToStorage,
} from './auth-utils';
export type { KeycloakUserClaims } from './auth-utils';
export { resolveKeycloakUser } from './keycloak-user';

const keycloakConfig = {
	url: process.env.REACT_APP_KEYCLOAK_URL ?? '',
	realm: process.env.REACT_APP_KEYCLOAK_REALM ?? '',
	clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID ?? '',
};

export const keycloak = new Keycloak(keycloakConfig);

let initPromise: Promise<boolean> | null = null;

/** Debe coincidir con Valid Redirect URIs registradas en Keycloak. */
export function getAppRedirectUri(): string {
	return `${window.location.origin}/`;
}

export function getSilentCheckSsoRedirectUri(): string {
	return new URL('/silent-check-sso.html', window.location.origin).toString();
}

export function hasOAuthCallbackParams(): boolean {
	const { search, hash } = window.location;
	return (
		/[?&#](code|state|session_state|error)=/.test(search) ||
		/[?&#](code|state|session_state|error)=/.test(hash)
	);
}

function cleanOAuthUrl(): void {
	if (!hasOAuthCallbackParams()) {
		return;
	}
	window.history.replaceState(window.history.state, '', getAppRedirectUri());
}

/** Tras logout se omite silent SSO para evitar cuelgues al volver a /. */
export const POST_LOGOUT_FLAG = 'auth_post_logout';

export function markPostLogout(): void {
	sessionStorage.setItem(POST_LOGOUT_FLAG, '1');
}

export function consumePostLogoutFlag(): boolean {
	const flagged = sessionStorage.getItem(POST_LOGOUT_FLAG) === '1';
	if (flagged) {
		sessionStorage.removeItem(POST_LOGOUT_FLAG);
	}
	return flagged;
}

export interface InitKeycloakOptions {
	skipSilentSso?: boolean;
}

/**
 * Initializes Keycloak once (check-sso + PKCE). Must run before React render on OAuth callback.
 */
export function initKeycloak(options: InitKeycloakOptions = {}): Promise<boolean> {
	if (initPromise) {
		return initPromise;
	}

	const isOAuthCallback = hasOAuthCallbackParams();
	const skipSilentSso = options.skipSilentSso === true;
	const storedSession = readStoredKeycloakSession();
	const hasStoredSession = hasStoredKeycloakSession();

	const initOptions: Keycloak.KeycloakInitOptions = {
		onLoad: 'check-sso',
		pkceMethod: 'S256',
		flow: 'standard',
		checkLoginIframe: false,
		redirectUri: getAppRedirectUri(),
		silentCheckSsoFallback: true,
	};

	if (storedSession.token) {
		initOptions.token = storedSession.token;
		if (storedSession.refreshToken) {
			initOptions.refreshToken = storedSession.refreshToken;
		}
		if (storedSession.idToken) {
			initOptions.idToken = storedSession.idToken;
		}
	}

	// Con tokens guardados no hace falta silent SSO; solo si no hay sesión local.
	if (!isOAuthCallback && !skipSilentSso && !hasStoredSession) {
		initOptions.silentCheckSsoRedirectUri = getSilentCheckSsoRedirectUri();
	}

	if (process.env.NODE_ENV === 'development') {
		initOptions.enableLogging = true;
	}

	const runInit = (): Promise<boolean> =>
		keycloak.init(initOptions).then((authenticated) => {
			if (authenticated) {
				persistKeycloakSession(keycloak);
				cleanOAuthUrl();
			} else if (isOAuthCallback) {
				cleanOAuthUrl();
				console.error(
					'Callback OAuth en URL pero Keycloak no autenticó. Revise redirect URI y clientId.',
					{ redirectUri: getAppRedirectUri(), clientId: keycloakConfig.clientId }
				);
			} else if (hasStoredSession) {
				syncTokenToStorage(null);
			}
			return authenticated;
		});

	// No cortar el intercambio del #code= ni la restauración desde localStorage.
	if (isOAuthCallback || skipSilentSso || hasStoredSession) {
		initPromise = runInit().catch((error) => {
			initPromise = null;
			if (isOAuthCallback) {
				cleanOAuthUrl();
			}
			console.error('Keycloak init falló', error);
			throw error;
		});
	} else {
		const INIT_TIMEOUT_MS = 12000;
		initPromise = Promise.race([
			runInit(),
			new Promise<boolean>((_, reject) => {
				window.setTimeout(
					() => reject(new Error('KEYCLOAK_INIT_TIMEOUT')),
					INIT_TIMEOUT_MS
				);
			}),
		]).catch((error) => {
			initPromise = null;
			if (error instanceof Error && error.message === 'KEYCLOAK_INIT_TIMEOUT') {
				console.warn('Keycloak init excedió tiempo de espera; se muestra login.');
				return false;
			}
			console.error('Keycloak init falló', error);
			throw error;
		});
	}

	return initPromise;
}

/** Reinicia el adapter para un nuevo ciclo login/logout. */
export function resetKeycloakInit(): void {
	initPromise = null;
}

export async function getAccessToken(minValidity = 70): Promise<string | null> {
	if (!keycloak.authenticated) {
		return null;
	}

	try {
		await keycloak.updateToken(minValidity);
	} catch {
		return null;
	}

	const token = keycloak.token ?? null;
	persistKeycloakSession(keycloak);
	return token;
}

export function loginWithGoogle(selectAccount = false): Promise<void> {
	const options: Record<string, string> = {
		idpHint: 'google',
		redirectUri: getAppRedirectUri(),
	};

	if (selectAccount) {
		options.prompt = 'select_account';
	}

	return keycloak.login(options as Keycloak.KeycloakLoginOptions);
}

export function logoutKeycloak(): Promise<void> {
	syncTokenToStorage(null);
	return keycloak.logout({
		redirectUri: getAppRedirectUri(),
	});
}
